import IconCheckmark from 'assets/icons/checkmark.svg';
import cn from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FUNNEL_STEPS } from '../constants';
import { useSubscriptionFunnel } from '../state';
import styles from './progress.module.css';

export const Progress: React.FC = () => {
  const {
    step: currentStep,
    goToStep,
    isSingleProductPurchase,
    isExistingSubscription,
  } = useSubscriptionFunnel();

  const funnelStepValues = Object.values(FUNNEL_STEPS);

  // dynamic progress steps because regular ABO funnel and updating existing subscription has different final step
  const funnelStepKeys = Object.keys(FUNNEL_STEPS).filter((step) => {
    if (isExistingSubscription) {
      return step !== 'PAYMENT';
    }

    return step !== 'CONFIRMATION';
  });

  const values = React.useMemo(() => {
    return funnelStepValues.filter((step) => {
      if (isExistingSubscription) {
        return step !== 'payment';
      }

      return step !== 'confirmation';
    });
  }, [isExistingSubscription, funnelStepValues]);

  const currentStepIndex = values.indexOf(currentStep);

  if (isSingleProductPurchase) {
    return <div />;
  }

  return (
    <div className="container lg:pb-4">
      <div
        data-test="progress-bar"
        className="bg-grey-200 -mx-4 flex lg:mx-0 lg:w-full lg:bg-transparent"
      >
        {funnelStepKeys.map((step, index) => {
          const stepValue = FUNNEL_STEPS[step as keyof typeof FUNNEL_STEPS];
          const isStepDone = currentStepIndex > values.indexOf(stepValue);
          const isStepComing = currentStepIndex < values.indexOf(stepValue);

          if (stepValue === 'start') return <React.Fragment key={step}></React.Fragment>;

          return (
            <React.Fragment key={step}>
              <button
                data-test="progress-step"
                className={cn(
                  'lex-shrink focus-default border-other-white text-other-white lg:text-primary-main flex h-8 w-full flex-col items-center justify-center border-r pt-1 font-black tracking-wider uppercase hover:opacity-75 lg:h-auto lg:w-8 lg:border-0 lg:bg-transparent lg:text-lg lg:tracking-normal lg:normal-case',
                  currentStep === stepValue
                    ? 'bg-primary-main pointer-events-none'
                    : 'lg:font-normal',
                  styles.step,
                  isStepComing ? 'lg:text-text-light-bg-secondary pointer-events-none' : '',
                  isStepDone ? 'bg-primary-main' : ''
                )}
                onClick={() => {
                  if (currentStepIndex > index) {
                    goToStep(stepValue);
                  }
                }}
              >
                <div
                  className={cn(
                    'mb-1 hidden h-8 w-8 items-center justify-center rounded-full border pt-px text-center text-base lg:flex',
                    currentStep === stepValue &&
                      'border-primary-main bg-primary-main text-text-dark-bg-contrast-white',
                    isStepComing && 'border-grey-300 text-text-light-bg-secondary',
                    isStepDone && 'border-primary-main'
                  )}
                >
                  {currentStepIndex > index ? <IconCheckmark className="h-4 w-auto" /> : index}
                </div>
                <span className="inline-block">
                  <FormattedMessage
                    id={`subscription-funnel:step:${
                      isExistingSubscription && stepValue === 'overview' ? 'confirm' : stepValue
                    }`}
                  />
                </span>
              </button>
              {index < values.length - 1 && (
                <div className="mb-6 hidden w-full items-center px-1 lg:flex">
                  {currentStepIndex > index ? (
                    <div className="border-primary-main w-full border"></div>
                  ) : (
                    <div className="border-grey-300 w-full border"></div>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
